// Client API verso le Pages Functions.

export class ApiError extends Error {
  constructor(
    public status: number,
    public code?: string,
    // Secondi suggeriti prima di riprovare (header Retry-After su 429). Usato
    // dalla coda multi-upload per messaggi "attendi N min". null se assente.
    public retryAfterSeconds: number | null = null,
  ) {
    super(code ?? `HTTP ${status}`);
    this.name = "ApiError";
  }
}

/** Parsa Retry-After (secondi) in modo prudente; null se assente/non valido. */
function readRetryAfter(res: Response): number | null {
  const raw = res.headers.get("Retry-After");
  if (!raw) return null;
  const n = Number(raw.trim());
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : null;
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...init,
    headers: { "content-type": "application/json", ...init?.headers },
  });
  if (!res.ok) {
    let code: string | undefined;
    try {
      code = ((await res.json()) as { error?: string }).error;
    } catch {
      /* corpo non JSON */
    }
    throw new ApiError(res.status, code);
  }
  return (await res.json()) as T;
}

export interface PrivateModeInfo {
  active: boolean;
  expiresAt?: string | null;
}

export interface SessionInfo {
  authenticated: boolean;
  username?: string;
  expiresAt?: string;
  privateMode?: PrivateModeInfo;
}

export interface PhotoSummary {
  id: string;
  latitude: number;
  longitude: number;
  marker_group_key: string;
  description: string | null;
  taken_at: string | null;
  mime_type: string;
  is_private: number;
  // Categorie (non sensibili): sempre presenti, anche senza private mode.
  categories: string[];
  place_label: string | null;
  // Gender (private-only): presenti SOLO quando la private mode è attiva.
  tag_male?: number;
  tag_female?: number;
  created_at: string;
}

export function getSession(): Promise<SessionInfo> {
  return apiFetch<SessionInfo>("/auth/session");
}

export function login(username: string, password: string): Promise<SessionInfo> {
  return apiFetch<SessionInfo>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
}

export function logout(): Promise<unknown> {
  return apiFetch("/auth/logout", { method: "POST" });
}

export async function listPhotos(): Promise<PhotoSummary[]> {
  const res = await apiFetch<{ photos: PhotoSummary[] }>("/photos");
  return res.photos;
}

export type StorageState = "ok" | "warning" | "full";

export interface StorageInfo {
  usedBytes: number;
  warningBytes: number;
  hardLimitBytes: number;
  maxPhotoBytes: number;
  state: StorageState;
}

// Il POST /api/photos restituisce già il riepilogo completo della foto creata
// (oltre allo storage): lo tipizziamo per poter aggiornare la UI in modo
// ottimistico senza dover rifare subito una GET.
export interface UploadResult {
  id: string;
  latitude: number;
  longitude: number;
  marker_group_key: string;
  description: string | null;
  taken_at: string | null;
  mime_type: string;
  is_private: number;
  categories: string[];
  place_label: string | null;
  tag_male?: number;
  tag_female?: number;
  created_at: string;
  storage: StorageInfo;
}

export function getStorage(): Promise<StorageInfo> {
  return apiFetch<StorageInfo>("/storage");
}

/** Upload foto (multipart). Niente content-type manuale: lo imposta il browser. */
export async function uploadPhoto(form: FormData): Promise<UploadResult> {
  const res = await fetch("/api/photos", {
    method: "POST",
    credentials: "same-origin",
    body: form,
  });
  if (!res.ok) {
    let code: string | undefined;
    try {
      code = ((await res.json()) as { error?: string }).error;
    } catch {
      /* corpo non JSON */
    }
    throw new ApiError(res.status, code, readRetryAfter(res));
  }
  return (await res.json()) as UploadResult;
}

export interface PhotoPatch {
  description?: string | null;
  latitude?: number;
  longitude?: number;
  taken_at?: string | null;
  is_private?: boolean;
  categories?: string[];
  place_label?: string | null;
  // Gender (private-only): applicati dal backend solo se la private mode è attiva.
  tag_male?: boolean;
  tag_female?: boolean;
}

export function updatePhoto(id: string, patch: PhotoPatch): Promise<{ photo: PhotoSummary }> {
  return apiFetch<{ photo: PhotoSummary }>(`/photos/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
}

export function deletePhoto(id: string): Promise<{ id: string; deleted: boolean }> {
  return apiFetch<{ id: string; deleted: boolean }>(`/photos/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function unlockPrivate(pin: string): Promise<{ privateMode: PrivateModeInfo }> {
  return apiFetch<{ privateMode: PrivateModeInfo }>("/private/unlock", {
    method: "POST",
    body: JSON.stringify({ pin }),
  });
}

export function lockPrivate(): Promise<{ privateMode: PrivateModeInfo }> {
  return apiFetch<{ privateMode: PrivateModeInfo }>("/private/lock", { method: "POST" });
}

/** URL (relativo, gated) del media di una foto. Mai un URL pubblico R2. */
export function mediaUrl(id: string): string {
  return `/api/media/${encodeURIComponent(id)}`;
}

export interface GeoResult {
  label: string;
  lat: number;
  lng: number;
}

/** Ricerca esplicita di un luogo via proxy same-origin (mai Nominatim diretto dal browser). */
export async function searchPlace(query: string): Promise<GeoResult[]> {
  const res = await apiFetch<{ results: GeoResult[] }>(`/geo?q=${encodeURIComponent(query)}`);
  return res.results;
}
